import {salesTaxRate} from './constants';
import {Invoice, InvoiceLineItem, InvoiceLineItemType} from 'shared/src/models';

export const getInvoiceTotal = (lineItems: InvoiceLineItem[]): number => {
	return getInvoiceSubtotal(lineItems) + getInvoiceTaxes(lineItems);
};

/**
 * A subtotal doesn't include payments, it shows the COST of the invoice not what is owed
 */
export const getInvoiceSubtotal = (lineItems: InvoiceLineItem[]): number => {
	return lineItems.reduce((subtotal, item) => {
		const itemCost = getLineItemCost(item);

		switch (item.type) {
			case InvoiceLineItemType.Cost:
				return subtotal + itemCost;
			case InvoiceLineItemType.Credit:
				return subtotal - itemCost;
			case InvoiceLineItemType.Payment:
				return subtotal;
		}

		throw new Error('A new InvoiceLineItemType was added but not handled');
	}, 0);
};

export const getInvoiceTaxes = (lineItems: InvoiceLineItem[]): number => {
	return lineItems.reduce((taxes, item) => {
		return taxes + Math.round(item.taxed ? getLineItemCost(item) * salesTaxRate : 0);
	}, 0);
};

export const getInvoiceAmountDue = (lineItems: InvoiceLineItem[]): number => {
	const totalCost = getInvoiceTotal(lineItems);
	const totalPaid = lineItems
		.filter((item) => item.type === InvoiceLineItemType.Payment)
		.reduce((total, item) => total + getLineItemCost(item), 0);

	return totalCost - totalPaid;
};

export const getLineItemCost = (lineItem: InvoiceLineItem) =>
	Math.round((lineItem.quantity ?? 1) * lineItem.price);

export const areInvoicesEqual = (a: Invoice, b: Invoice): boolean => {
	const aItems = a.items.filter((item) => !isLineItemEmpty(item));
	const bItems = b.items.filter((item) => !isLineItemEmpty(item));

	if (aItems.length !== bItems.length) {
		return false;
	}

	return (
		a.description === b.description &&
		a.date.equals(b.date) &&
		a.customer === b.customer &&
		aItems.every((aItem, index) => {
			const bItem = bItems[index];

			return (
				aItem.name === bItem.name &&
				aItem.description === bItem.description &&
				aItem.type === bItem.type &&
				aItem.quantity === bItem.quantity &&
				aItem.price === bItem.price &&
				aItem.taxed === bItem.taxed
			);
		})
	);
};

export const isInvoiceValid = (invoice: Invoice): boolean => {
	if (!invoice.description.trim()) {
		return false;
	}

	if (invoice.items.every(isLineItemEmpty)) {
		return false;
	}

	if (invoice.items.some((lineItem) => !isLineItemEmpty(lineItem) && !isLineItemValid(lineItem))) {
		return false;
	}

	return true;
};

const isLineItemValid = (lineItem: InvoiceLineItem) => {
	if (isLineItemEmpty(lineItem)) {
		return false;
	}

	if (!lineItem.name.trim() || lineItem.price <= 0) {
		return false;
	}

	if (typeof lineItem.quantity === 'number' && lineItem.quantity <= 0) {
		return false;
	}

	return true;
};

export const isLineItemEmpty = (lineItem: InvoiceLineItem): boolean => {
	return !lineItem.name.trim();
};
